"use client";

import React, { useEffect, useRef, useState } from "react";
import { FileCheck, GraduationCap, ScrollText, ShieldCheck, ArrowRight } from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const documentsData = [{
  title: "Antecedentes Penales Federales",
  description: "Tramitamos tu constancia de antecedentes no penales federales de manera rápida, segura y confidencial.",
  icon: FileCheck,
  link: "/Apostilles",
  linkText: "Ver detalles"
}, {
  title: "Actas de Nacimiento, Matrimonio y Defunción",
  description: "Obtención y actualización de actas de nacimiento, matrimonio, defuncion y solteria, listas para trámites en el extranjero.",
  icon: ScrollText,
  link: "/MexicanDocumentApostilles",
  linkText: "Solicitar acta"
}, {
  title: "Poderes y Actas Notariales",
  description: "Tramitamos la apostilla de tu documentación legal o de tu empresa para que tenga valides en el resto del mundo.",
  icon: ShieldCheck,
  link: "/MexicanDocumentApostilles",
  linkText: "Iniciar trámite"
}, {
  title: "Títulos Profesionales",
  description: "Gestión de legalización y apostilla de títulos, cédulas y certificados de estudios para validar tu educación en el exterior.",
  icon: GraduationCap,
  link: "/Apostilles",
  linkText: "Más información"
}];
export default function Documentos() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section className="py-24 px-6 bg-background overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div ref={headerRef} className={`text-center mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 font-sans tracking-tight">
            Documentación Oficial Sin Complicaciones
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Gestionamos tus trámites más importantes con la seriedad y rapidez que necesitas, sin importar en qué parte del mundo te encuentres.
          </p>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 transition-all duration-1000 delay-200 ease-out ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {documentsData.map((doc, i) => <Card key={i} data-index={i} className="h-full flex flex-col border border-border/50 hover:shadow-lg transition-shadow duration-300 group">
              <CardHeader>
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 text-primary group-hover:scale-110 transition-transform duration-300">
                  <doc.icon className="size-6" />
                </div>
                <CardTitle className="text-xl font-bold text-foreground leading-tight">
                  {doc.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-grow">
                <CardDescription className="text-muted-foreground text-base leading-relaxed">
                  {doc.description}
                </CardDescription>
              </CardContent>
              <CardFooter>
                <Button asChild variant="outline" className="w-full group/btn border-primary/20 hover:border-primary hover:bg-primary/5 text-primary">
                  <Link to={doc.link} className="flex items-center justify-center gap-2">
                    {doc.linkText}
                    <ArrowRight className="size-4 group-hover/btn:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>)}
        </div>
      </div>
    </section>;
}